import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'

interface PasswordForm {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export default function ProfilePage() {
  const user = useAuthStore((s) => s.user)
  const [pwSuccess, setPwSuccess] = useState(false)

  const pwForm = useForm<PasswordForm>()
  const changePassword = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      api.patch('/users/me/password', data),
    onSuccess: () => {
      pwForm.reset()
      setPwSuccess(true)
      setTimeout(() => setPwSuccess(false), 3000)
    },
  })

  const onSubmit = pwForm.handleSubmit((data) => {
    if (data.newPassword !== data.confirmPassword) {
      pwForm.setError('confirmPassword', { message: 'Пароли не совпадают' })
      return
    }
    changePassword.mutate({ currentPassword: data.currentPassword, newPassword: data.newPassword })
  })

  return (
    <div className="p-6 max-w-md mx-auto">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Профиль</h2>

      <section className="bg-white border border-gray-200 rounded-2xl p-5 mb-4">
        <h3 className="font-semibold text-gray-700 mb-3">Данные</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Имя</span>
            <span className="font-medium">{user?.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Телефон</span>
            <span className="font-medium">{user?.phone}</span>
          </div>
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h3 className="font-semibold text-gray-700 mb-3">Сменить пароль</h3>

        {pwSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-3 text-sm text-green-700 font-medium">
            ✅ Пароль изменён
          </div>
        )}
        {changePassword.isError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 text-sm text-red-700">
            {(changePassword.error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Ошибка смены пароля'}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Текущий пароль</label>
            <input
              type="password"
              autoComplete="current-password"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              {...pwForm.register('currentPassword', { required: 'Обязательное поле' })}
            />
            {pwForm.formState.errors.currentPassword && (
              <p className="text-xs text-red-500 mt-1">{pwForm.formState.errors.currentPassword.message}</p>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Новый пароль</label>
            <input
              type="password"
              autoComplete="new-password"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              {...pwForm.register('newPassword', { required: 'Обязательное поле', minLength: { value: 6, message: 'Минимум 6 символов' } })}
            />
            {pwForm.formState.errors.newPassword && (
              <p className="text-xs text-red-500 mt-1">{pwForm.formState.errors.newPassword.message}</p>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Повторите новый пароль</label>
            <input
              type="password"
              autoComplete="new-password"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              {...pwForm.register('confirmPassword', { required: 'Обязательное поле' })}
            />
            {pwForm.formState.errors.confirmPassword && (
              <p className="text-xs text-red-500 mt-1">{pwForm.formState.errors.confirmPassword.message}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={changePassword.isPending}
            className="w-full bg-brand-600 text-white py-3 rounded-xl text-sm font-medium disabled:opacity-60"
          >
            {changePassword.isPending ? 'Сохранение...' : 'Сменить пароль'}
          </button>
        </form>
      </section>
    </div>
  )
}
